import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ShieldAlert,
  Activity,
  Gauge,
  Layers,
  AlertTriangle,
  Coins,
  ListTree,
} from 'lucide-react';
import { cn } from '../lib/utils';

// ============== Shared loan shapes (kept in sync with PerformanceAttributionScreen) ==============
export interface PcLlAttributionLoanLite {
  loanSource: string;
  loanId: number;
  securityId: number;
  loanName: string;
  borrowerName: string;
  sponsorName?: string;
  sector?: string;
  facilityType?: string;
  cashInterest: number;
  oidAccretion: number;
  feeIncome: number;
  pikIncome: number;
  carryPnl: number;
  principalPnl: number;
  realizedPnl: number;
  unrealizedPnlChange: number;
  residual: number;
  totalPnl: number;
  totalReturn: number;
  periodExpectedLoss: number;
  economicCapital: number;
  riskAdjustedPnl: number;
  raroc: number;
  beginningMarketValue: number;
  endingMarketValue: number;
  lifecycleDrawdownCount: number;
  lifecyclePaydownCount: number;
  lifecycleAmendmentCount: number;
}

export interface LoanRiskRow {
  loanSource: string;
  loanId: number;
  securityId: number;
  loanName: string;
  borrowerName: string;
  sector?: string;
  facilityType?: string;
  seniority?: string;
  rating?: string;
  fundedExposure: number;
  unfundedCommitment: number;
  commitmentAmount: number;
  exposureAtDefault: number;
  probabilityDefault: number; // decimal, e.g. 0.03 = 3%
  lossGivenDefault: number; // decimal
  recoveryRate: number; // decimal
  expectedLoss: number; // dollars
  expectedLossPctOfFundedExposure: number; // decimal
  spreadBps: number; // bps
  allInRate: number; // percent units, e.g. 8.60 = 8.60% (NOT a decimal — do not ×100)
  spreadDuration: number; // years
  spreadDv01: number; // dollars
  baseRateDuration: number; // years
  baseRateDv01: number; // dollars
  defaultStressLoss: number; // dollars
  economicCapital: number; // dollars
  pikElectionCurrent?: boolean;
  nonAccrual?: boolean;
  covenantName?: string;
  covenantCushionValue?: number;
  covenantCushionPct?: number;
  covenantStatus?: string;
  // ---- Input-provenance + economic-capital decomposition (newer responses; all optional) ----
  pdSource?: string;
  lgdSource?: string;
  eadSource?: string;
  durationSource?: string;
  expectedLossHorizon?: string; // e.g. "1Y"
  economicCapitalConfidenceZ?: number | null; // e.g. 2.33; null when PD≥1 or PD=0
  economicCapitalDefaultVolatility?: number | null; // √(PD·(1−PD)); null when degenerate
}

// ============== Formatters ==============
// Full-dollar formatter with smart magnitude suffix, matches the screen's $1.2M / $37.7K style.
const money = (v: number | null | undefined): string => {
  if (v == null || isNaN(v as number)) return '—';
  const n = v as number;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

// Decimal (0.03) → percent string
const pctDec = (v: number | null | undefined, digits = 2): string =>
  v == null || isNaN(v as number) ? '—' : `${((v as number) * 100).toFixed(digits)}%`;

const yearsFmt = (v: number | null | undefined): string =>
  v == null || isNaN(v as number) ? '—' : `${(v as number).toFixed(2)}y`;

// ============== Reusable explain card primitives ==============
const ExplainCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  accent: string;
  formula: string;
  children: React.ReactNode;
}> = ({ icon, title, accent, formula, children }) => (
  <div className="bg-slate-950/40 border border-white/5 rounded-2xl overflow-hidden">
    <div className="flex items-center gap-2.5 px-5 py-3 border-b border-white/5 bg-slate-900/40">
      <div
        className="w-7 h-7 rounded-lg flex items-center justify-center border border-white/5"
        style={{ backgroundColor: `${accent}1a`, color: accent }}
      >
        {icon}
      </div>
      <h4 className="text-sm font-bold text-white tracking-tight">{title}</h4>
    </div>
    <div className="px-5 py-4 space-y-3">
      <div className="font-mono text-[11px] text-slate-400 bg-black/30 rounded-lg px-3 py-2 border border-white/5 leading-relaxed">
        {formula}
      </div>
      {children}
    </div>
  </div>
);

// One input row: label on the left, plugged-in value on the right.
const InputRow: React.FC<{ label: string; value: string; muted?: boolean }> = ({ label, value, muted }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-slate-500 font-medium">{label}</span>
    <span className={cn('font-mono font-bold', muted ? 'text-slate-400' : 'text-slate-200')}>{value}</span>
  </div>
);

// The literal "= ..." reconciliation line.
const LiteralLine: React.FC<{ children: React.ReactNode; tone?: 'pos' | 'neg' | 'neutral' }> = ({ children, tone = 'neutral' }) => (
  <div
    className={cn(
      'font-mono text-[11px] mt-1 px-3 py-2 rounded-lg border leading-relaxed break-words',
      tone === 'pos' && 'text-emerald-300 bg-emerald-500/5 border-emerald-500/20',
      tone === 'neg' && 'text-red-300 bg-red-500/5 border-red-500/20',
      tone === 'neutral' && 'text-blue-300 bg-blue-500/5 border-blue-500/20'
    )}
  >
    {children}
  </div>
);

interface LoanExplainDrawerProps {
  loan: PcLlAttributionLoanLite | null;
  risk: LoanRiskRow | null;
  onClose: () => void;
}

const LoanExplainDrawer: React.FC<LoanExplainDrawerProps> = ({ loan, risk, onClose }) => {
  // Open from either context: the attribution view passes `loan` (+ matched `risk`),
  // the risk view passes `risk` alone (no per-loan P&L).
  const open = loan != null || risk != null;

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const sourceLabel = (loan?.loanSource ?? risk?.loanSource) === 'PRIVATE_CREDIT' ? 'PC' : 'LL';

  // Header identity falls back to the risk row when there's no attribution loan.
  const borrowerName = loan?.borrowerName ?? risk?.borrowerName ?? '';
  const loanName = loan?.loanName ?? risk?.loanName ?? '';
  const sponsorName = loan?.sponsorName;
  const headerSector = loan?.sector ?? risk?.sector;

  // ---- Card 4: P&L decomposition components (exact backend identity) ----
  const pnlComponents = loan
    ? [
        { label: 'Carry (cash interest)', value: loan.cashInterest },
        { label: 'PIK income', value: loan.pikIncome },
        { label: 'OID accretion', value: loan.oidAccretion },
        { label: 'Fee income', value: loan.feeIncome },
        { label: 'Mark movement (unrealized)', value: loan.unrealizedPnlChange },
        { label: 'Realized principal', value: loan.realizedPnl },
        { label: 'Residual (unattributed)', value: loan.residual },
      ]
    : [];
  const pnlSum = pnlComponents.reduce((acc, c) => acc + (c.value || 0), 0);
  // Reconciliation tolerance for floating-point noise.
  const pnlReconciles = loan ? Math.abs(pnlSum - loan.totalPnl) < 1 : false;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Overlay */}
          <motion.div
            key="loan-explain-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
          />
          {/* Slide-over panel */}
          <motion.div
            key="loan-explain-panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 34 }}
            className="fixed top-0 right-0 z-[61] h-full w-full max-w-[560px] bg-slate-950 border-l border-white/10 shadow-2xl flex flex-col"
            role="dialog"
            aria-modal="true"
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-white/10 bg-slate-900/60 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-purple-500/10 text-purple-300 border border-purple-500/20">
                    {sourceLabel}
                  </span>
                  {risk?.rating && (
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-slate-700/40 text-slate-300 border border-white/10">
                      {risk.rating}
                    </span>
                  )}
                  {risk?.nonAccrual && (
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-red-500/10 text-red-300 border border-red-500/20">
                      Non-Accrual
                    </span>
                  )}
                </div>
                <h2 className="text-lg font-bold text-white tracking-tight truncate">
                  {borrowerName || loanName}
                </h2>
                <p className="text-[11px] text-slate-500 font-medium mt-0.5 truncate">
                  {loanName}
                  {sponsorName ? ` · ${sponsorName}` : ''}
                </p>
                <p className="text-[10px] text-slate-600 font-bold uppercase tracking-widest mt-1.5">
                  {[headerSector, loan?.facilityType || risk?.facilityType, risk?.seniority].filter(Boolean).join(' · ') || '—'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="flex-shrink-0 w-9 h-9 rounded-xl bg-slate-800 border border-white/5 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                Explain Every Number
              </div>

              {/* ===== Card 1 — Expected Loss ===== */}
              <ExplainCard
                icon={<ShieldAlert className="w-4 h-4" />}
                title="Expected Loss"
                accent="#f59e0b"
                formula="EL = PD × LGD × EAD"
              >
                {risk ? (
                  <>
                    <div className="space-y-1.5">
                      <InputRow
                        label={`PD — probability of default${risk.expectedLossHorizon ? ` (${risk.expectedLossHorizon})` : ''}`}
                        value={pctDec(risk.probabilityDefault, 2)}
                      />
                      <InputRow label="LGD — loss given default" value={pctDec(risk.lossGivenDefault, 1)} />
                      <InputRow label="EAD — exposure at default" value={money(risk.exposureAtDefault)} />
                      {risk.expectedLossHorizon && (
                        <InputRow label="PD horizon" value={risk.expectedLossHorizon} muted />
                      )}
                    </div>
                    <LiteralLine tone="neg">
                      {money(risk.expectedLoss)} = {pctDec(risk.probabilityDefault, 2)} × {pctDec(risk.lossGivenDefault, 1)} ×{' '}
                      {money(risk.exposureAtDefault)}
                    </LiteralLine>
                    {risk.nonAccrual && (
                      <div className="flex items-start gap-2 text-[11px] text-amber-300/90 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span>
                          {risk.pdSource
                            ? `${risk.pdSource}; LGD from the impairment mark.`
                            : 'Distressed override applied — LGD from the impairment mark.'}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-[11px] text-slate-500 italic py-1">Risk metrics unavailable for this loan.</div>
                )}
              </ExplainCard>

              {/* ===== Card 2 — Spread DV01 ===== */}
              <ExplainCard
                icon={<Activity className="w-4 h-4" />}
                title="Spread DV01"
                accent="#3b82f6"
                formula="Spread DV01 = Funded Exposure × Spread Duration × 1bp"
              >
                {risk ? (
                  <>
                    <div className="space-y-1.5">
                      <InputRow label="Funded exposure" value={money(risk.fundedExposure)} />
                      <InputRow label="Spread duration" value={yearsFmt(risk.spreadDuration)} />
                      <InputRow label="1bp" value="0.0001" muted />
                    </div>
                    <LiteralLine tone="neutral">
                      {money(risk.spreadDv01)} = {money(risk.fundedExposure)} × {yearsFmt(risk.spreadDuration)} × 0.0001
                    </LiteralLine>
                    <div className="flex items-center justify-between text-[11px] pt-1 border-t border-white/5 mt-1">
                      <span className="text-slate-500 font-medium">
                        Base-rate DV01 ({yearsFmt(risk.baseRateDuration)})
                      </span>
                      <span className="font-mono font-bold text-slate-300">{money(risk.baseRateDv01)}</span>
                    </div>
                    {risk.spreadBps != null && (
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-500 font-medium">Spread / all-in rate</span>
                        <span className="font-mono font-bold text-slate-300">
                          {risk.spreadBps.toFixed(0)}bps /{' '}
                          {risk.allInRate != null ? `${risk.allInRate.toFixed(2)}%` : '—'}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-[11px] text-slate-500 italic py-1">Risk metrics unavailable for this loan.</div>
                )}
              </ExplainCard>

              {/* ===== Card 3 — RAROC ===== */}
              <ExplainCard
                icon={<Gauge className="w-4 h-4" />}
                title="RAROC"
                accent="#14b8a6"
                formula="RAROC = (Total P&L − Period Expected Loss) ÷ Economic Capital"
              >
                {loan ? (
                  <>
                    <div className="space-y-1.5">
                      <InputRow label="Total P&L" value={money(loan.totalPnl)} />
                      <InputRow label="Period expected loss" value={money(loan.periodExpectedLoss)} />
                      <InputRow label="Economic capital" value={money(loan.economicCapital)} />
                      <div className="flex items-center justify-between text-[11px] pt-1.5 border-t border-white/5 mt-1.5">
                        <span className="text-slate-500 font-medium">Risk-adjusted P&L (Total − EL)</span>
                        <span className={cn('font-mono font-bold', loan.riskAdjustedPnl >= 0 ? 'text-emerald-400' : 'text-red-400')}>
                          {money(loan.riskAdjustedPnl)}
                        </span>
                      </div>
                    </div>
                    {/* Period bridge: annual EL → period EL → RAROC numerator */}
                    {risk?.expectedLoss != null && (
                      <div className="mt-2 rounded-lg border border-white/5 bg-black/20 px-3 py-2 space-y-1.5">
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                          Annual → period bridge
                        </div>
                        <InputRow
                          label={`Annual expected loss${risk.expectedLossHorizon ? ` (${risk.expectedLossHorizon})` : ''}`}
                          value={money(risk.expectedLoss)}
                          muted
                        />
                        <div className="font-mono text-[11px] text-slate-400 flex items-center justify-between">
                          <span>× days/365 →</span>
                          <span className="font-bold text-slate-300">{money(loan.periodExpectedLoss)} period EL</span>
                        </div>
                        <div className="font-mono text-[11px] text-slate-400 flex items-center justify-between">
                          <span>Total P&L − period EL →</span>
                          <span className={cn('font-bold', loan.riskAdjustedPnl >= 0 ? 'text-emerald-400' : 'text-red-400')}>
                            {money(loan.riskAdjustedPnl)} numerator
                          </span>
                        </div>
                      </div>
                    )}
                    <LiteralLine tone={loan.raroc >= 0 ? 'pos' : 'neg'}>
                      {pctDec(loan.raroc, 2)} = ({money(loan.totalPnl)} − {money(loan.periodExpectedLoss)}) ÷{' '}
                      {money(loan.economicCapital)}
                    </LiteralLine>
                  </>
                ) : risk ? (
                  <>
                    <div className="space-y-1.5">
                      <InputRow
                        label={`Annual expected loss${risk.expectedLossHorizon ? ` (${risk.expectedLossHorizon})` : ''}`}
                        value={money(risk.expectedLoss)}
                      />
                      <InputRow label="Economic capital" value={money(risk.economicCapital)} />
                    </div>
                    <div className="text-[11px] text-slate-500 italic pt-1">
                      Period P&L and the annual → period EL bridge require the attribution context. Open this loan
                      from Return Attribution for the full RAROC walk.
                    </div>
                  </>
                ) : (
                  <div className="text-[11px] text-slate-500 italic py-1">RAROC inputs unavailable for this loan.</div>
                )}
              </ExplainCard>

              {/* ===== Card 3b — Economic Capital ===== */}
              <ExplainCard
                icon={<Coins className="w-4 h-4" />}
                title="Economic Capital"
                accent="#0ea5e9"
                formula={
                  risk && risk.economicCapitalConfidenceZ != null
                    ? 'EC = EAD × LGD × Z₉₉ × √(PD·(1−PD))'
                    : 'EC = EAD × LGD'
                }
              >
                {risk ? (
                  (() => {
                    const degenerate = risk.economicCapitalConfidenceZ == null;
                    const z = risk.economicCapitalConfidenceZ;
                    const vol = risk.economicCapitalDefaultVolatility;
                    return (
                      <>
                        <div className="space-y-1.5">
                          <InputRow label="EAD — exposure at default" value={money(risk.exposureAtDefault)} />
                          <InputRow label="LGD — loss given default" value={pctDec(risk.lossGivenDefault, 1)} />
                          {!degenerate && (
                            <>
                              <InputRow
                                label="Z₉₉ — confidence multiple"
                                value={z != null ? z.toFixed(2) : '—'}
                              />
                              <InputRow
                                label="√(PD·(1−PD)) — default volatility"
                                value={vol != null ? vol.toFixed(4) : '—'}
                              />
                            </>
                          )}
                        </div>
                        {degenerate ? (
                          <>
                            <div className="flex items-start gap-2 text-[11px] text-amber-300/90 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2">
                              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                              <span>
                                Degenerate PD (defaulted / PD≥1 or PD=0) — capital collapses to the loss-given-default
                                amount; no confidence-interval term.
                              </span>
                            </div>
                            <LiteralLine tone="neutral">
                              {money(risk.economicCapital)} = {money(risk.exposureAtDefault)} ×{' '}
                              {pctDec(risk.lossGivenDefault, 1)}
                            </LiteralLine>
                          </>
                        ) : (
                          <LiteralLine tone="neutral">
                            {money(risk.economicCapital)} = {money(risk.exposureAtDefault)} ×{' '}
                            {pctDec(risk.lossGivenDefault, 1)} × {z != null ? z.toFixed(2) : '—'} ×{' '}
                            {vol != null ? vol.toFixed(4) : '—'}
                          </LiteralLine>
                        )}
                      </>
                    );
                  })()
                ) : (
                  <div className="text-[11px] text-slate-500 italic py-1">Economic capital inputs unavailable for this loan.</div>
                )}
              </ExplainCard>

              {/* ===== Source of inputs ===== */}
              {risk && (risk.pdSource || risk.lgdSource || risk.eadSource || risk.durationSource) && (
                <div className="bg-slate-950/40 border border-white/5 rounded-2xl overflow-hidden">
                  <div className="flex items-center gap-2.5 px-5 py-3 border-b border-white/5 bg-slate-900/40">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center border border-white/5"
                      style={{ backgroundColor: '#64748b1a', color: '#94a3b8' }}
                    >
                      <ListTree className="w-4 h-4" />
                    </div>
                    <h4 className="text-sm font-bold text-white tracking-tight">Source of inputs</h4>
                  </div>
                  <div className="px-5 py-4 space-y-2">
                    {[
                      { key: 'PD', src: risk.pdSource },
                      { key: 'LGD', src: risk.lgdSource },
                      { key: 'EAD', src: risk.eadSource },
                      { key: 'Duration', src: risk.durationSource },
                    ]
                      .filter((row) => !!row.src)
                      .map((row) => (
                        <div key={row.key} className="flex items-start justify-between gap-3 text-xs">
                          <span className="font-mono font-bold text-slate-400 flex-shrink-0">{row.key} ←</span>
                          <span className="text-slate-300 text-right leading-snug">{row.src}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* ===== Card 4 — P&L Decomposition (marquee) ===== */}
              <ExplainCard
                icon={<Layers className="w-4 h-4" />}
                title="P&L Decomposition"
                accent="#a855f7"
                formula="Total P&L = Carry + PIK + OID + Fees + Mark + Realized + Residual"
              >
                {!loan ? (
                  <div className="text-[11px] text-slate-500 italic py-1">
                    P&L decomposition available in Return Attribution view — open this loan from Return Attribution to
                    see the per-component carry / PIK / OID / mark waterfall.
                  </div>
                ) : (
                <>
                <div className="space-y-2">
                  {pnlComponents.map((c) => {
                    const v = c.value || 0;
                    const isResidual = c.label.startsWith('Residual');
                    return (
                      <div key={c.label} className="flex items-center gap-3">
                        <div className="flex-1 flex items-center justify-between text-xs">
                          <span className={cn('font-medium', isResidual ? 'text-slate-500 italic' : 'text-slate-400')}>
                            {c.label}
                          </span>
                          <span
                            className={cn(
                              'font-mono font-bold',
                              v > 0 ? 'text-emerald-400' : v < 0 ? 'text-red-400' : 'text-slate-500'
                            )}
                          >
                            {v >= 0 ? '+' : ''}
                            {money(v)}
                          </span>
                        </div>
                        {/* Mini magnitude bar for visual waterfall feel */}
                        <div className="w-16 h-1.5 rounded-full bg-slate-800 overflow-hidden flex-shrink-0">
                          <div
                            className={cn('h-full rounded-full', v >= 0 ? 'bg-emerald-500/60' : 'bg-red-500/60')}
                            style={{
                              width: `${Math.min(
                                100,
                                pnlSum !== 0 ? (Math.abs(v) / Math.max(...pnlComponents.map((p) => Math.abs(p.value || 0)), 1)) * 100 : 0
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between pt-2.5 mt-1 border-t border-white/10 text-sm">
                  <span className="font-bold text-white">Total P&L</span>
                  <span className={cn('font-mono font-bold', loan.totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400')}>
                    {loan.totalPnl >= 0 ? '+' : ''}
                    {money(loan.totalPnl)}
                  </span>
                </div>
                <LiteralLine tone={pnlReconciles ? (loan.totalPnl >= 0 ? 'pos' : 'neg') : 'neutral'}>
                  Σ components = {money(pnlSum)}
                  {pnlReconciles ? ' ✓ reconciles to Total P&L' : ` (Total P&L ${money(loan.totalPnl)})`}
                </LiteralLine>
                </>
                )}
              </ExplainCard>

              {/* Lifecycle footnote */}
              {loan && (
                <div className="flex items-center justify-between text-[10px] font-bold text-slate-600 uppercase tracking-widest pt-1">
                  <span>
                    Begin {money(loan.beginningMarketValue)} → End {money(loan.endingMarketValue)}
                  </span>
                  <span>
                    {loan.lifecycleDrawdownCount}D / {loan.lifecyclePaydownCount}P / {loan.lifecycleAmendmentCount}A
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LoanExplainDrawer;
